import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// First-run product tour steps (used only by this component).
const TOUR_STEPS = [
  { id: 'home',
    title: 'Welcome to Monarch',
    body: "This is the home base. Birdseye and Daily live here — quick read of how the cafe is running right now." },
  { id: 'schedule',
    title: 'Schedule',
    body: "Your shifts, swaps, and time card. Managers approve time-off requests from the Approvals tab here." },
  { id: 'tasks',
    title: 'Tasks',
    body: "The opening, mid-shift, and closing checklists for the cafe. Tap to complete, or flag if something needs attention." },
  { id: 'operations',
    title: 'Operations',
    body: "Equipment status, temperature logs, maintenance, and the incident-report form. Use this if anything breaks or needs reporting." },
  { id: 'knowledgehub',
    title: 'Knowledge',
    body: "Recipes, policies and SOPs, alerts, and your training docs. Empire onboarding lives in here too." },
  { id: 'chat',
    title: 'Chat',
    body: "Direct messages with coworkers and managers. Image upload supported." },
  { id: 'team',
    title: 'Profile',
    body: "Your own record — onboarding paperwork, availability, emergency contact, and replay this tour anytime from here." },
];

export function WelcomeTour({ user, onClose }) {
  const [idx, setIdx] = useState(0);
  const [box, setBox] = useState(null);
  const step = TOUR_STEPS[idx];

  // Re-measure the target on every step + on window resize.
  useEffect(() => {
    function measure() {
      const el = document.querySelector(`[data-tour-id="${step.id}"]`);
      if (!el) { setBox(null); return; }
      const r = el.getBoundingClientRect();
      setBox({ top: r.top, left: r.left, width: r.width, height: r.height });
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [step.id]);

  async function finish() {
    try {
      await api('POST', '/api/me/tour/complete', {}, { 'x-user-id': String(user.id) });
    } catch {}
    onClose();
  }

  const cardStyle = (() => {
    // Default centered fallback when target isn't visible (mobile sidebar
    // may be off-screen, or selector missed).
    if (!box) {
      return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
    // Place to the right of the target on desktop; below on narrow screens.
    const narrow = window.innerWidth < 720;
    if (narrow) {
      return { position: 'fixed', top: Math.min(box.top + box.height + 12, window.innerHeight - 220),
               left: 16, right: 16 };
    }
    return { position: 'fixed', top: box.top, left: Math.min(box.left + box.width + 16, window.innerWidth - 360), width: 320 };
  })();

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, pointerEvents: 'auto' }}>
      {/* Dim backdrop with a cut-out around the target. We don't try to
          punch a hole through (clip-path) — a soft glow ring is simpler
          and reads as "look here" without complicating click handling. */}
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)' }} onClick={finish} />
      {box && (
        <div style={{
          position: 'absolute', top: box.top - 4, left: box.left - 4,
          width: box.width + 8, height: box.height + 8,
          border: '3px solid var(--gold)', borderRadius: 8,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0)', pointerEvents: 'none',
        }} />
      )}
      <div style={{ ...cardStyle, background: '#fff', borderRadius: 12, padding: 18, boxShadow: '0 10px 40px rgba(0,0,0,0.35)', maxWidth: 360 }}>
        <div style={{ fontSize: 11, color: '#9e9e9e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6 }}>
          Welcome tour — step {idx + 1} of {TOUR_STEPS.length}
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#212121', marginTop: 4 }}>
          {step.title}
        </div>
        <div style={{ fontSize: 13, color: '#37474f', marginTop: 6, lineHeight: 1.5 }}>
          {step.body}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
          <button onClick={finish}
            style={{ background: 'none', border: 'none', color: '#9e9e9e', fontSize: 12, cursor: 'pointer', padding: 0 }}>
            Skip
          </button>
          <div style={{ display: 'flex', gap: 6 }}>
            {idx > 0 && (
              <button onClick={() => setIdx((i) => i - 1)} className="btn-secondary" style={{ fontSize: 12 }}>
                Back
              </button>
            )}
            {idx < TOUR_STEPS.length - 1 ? (
              <button onClick={() => setIdx((i) => i + 1)} className="btn-primary" style={{ fontSize: 12 }}>
                Next
              </button>
            ) : (
              <button onClick={finish} className="btn-primary" style={{ fontSize: 12 }}>
                Finish
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
